import { motion } from "framer-motion";
import {
  Compass, ClipboardList, PenTool, Search, ShieldCheck, Truck, LifeBuoy,
} from "lucide-react";

const steps = [
  { name: "Discovery", icon: Compass, desc: "Understanding your goals, content, and technical requirements." },
  { name: "Planning", icon: ClipboardList, desc: "Defining workflows, schedules, formats, and delivery milestones." },
  { name: "Production", icon: PenTool, desc: "Content transformation, editing, typesetting, accessibility, and digital publishing." },
  { name: "Review", icon: Search, desc: "Client review, revisions, and collaborative feedback." },
  { name: "Quality Assurance", icon: ShieldCheck, desc: "Multi-stage validation, accessibility compliance, and technical verification." },
  { name: "Delivery", icon: Truck, desc: "Final assets delivered across print and digital formats." },
  { name: "Ongoing Support", icon: LifeBuoy, desc: "Updates, maintenance, enhancements, and long-term partnership." },
];

export default function Workflow() {
  return (
    <section id="workflow" data-testid="workflow-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="blob bg-[#00B8A9]/15 h-[280px] w-[280px] top-10 left-0" aria-hidden="true" />
      <div className="blob bg-[#1F5EFF]/15 h-[280px] w-[280px] bottom-10 right-0" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-2xl mb-14">
          <div className="overline">How We Work</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B1F3A] tracking-tighter leading-[1.08]">
            A proven process. Exceptional outcomes.
          </h2>
          <p className="mt-4 text-[#475569] text-base sm:text-lg">
            Every project follows a structured, collaborative workflow&mdash;from discovery and planning to
            production, quality assurance, delivery, and ongoing support&mdash;ensuring accuracy, consistency,
            accessibility, and on-time delivery.
          </p>
        </div>

        {/* Desktop: horizontal timeline */}
        <div className="hidden lg:block relative">
          <motion.div
            aria-hidden="true"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease: [0.2, 0.8, 0.2, 1] }}
            className="absolute left-0 right-0 top-[28px] h-px timeline-line origin-left"
          />
          <ol className="grid grid-cols-7 gap-3 list-none m-0 p-0">
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.li
                  key={s.name}
                  data-testid={`workflow-step-${i}`}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 + i * 0.18 }}
                  className="flex flex-col items-center text-center"
                >
                  <div aria-hidden="true" className="relative z-10 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white border border-[#0B1F3A]/10 shadow-[0_10px_30px_-18px_rgba(11,31,58,0.35)]">
                    <Icon className="h-5 w-5 text-[#1F5EFF]" />
                  </div>
                  <div className="mt-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#475569]">
                    Step {i + 1}
                  </div>
                  <h3 className="mt-1 font-display font-semibold text-[15px] text-[#0B1F3A]">
                    {s.name}
                  </h3>
                  <div className="mt-1 text-[12px] text-[#475569] leading-snug px-1">
                    {s.desc}
                  </div>
                </motion.li>
              );
            })}
          </ol>
        </div>

        {/* Mobile/tablet: vertical timeline */}
        <ol className="lg:hidden space-y-4 list-none m-0 p-0">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.li
                key={s.name}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="flex items-start gap-4 rounded-2xl border border-[#0B1F3A]/10 bg-white p-4"
              >
                <div aria-hidden="true" className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#1F5EFF]/10 text-[#1F5EFF] shrink-0">
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#475569]">
                    Step {i + 1}
                  </div>
                  <h3 className="font-display font-semibold text-[16px] text-[#0B1F3A]">{s.name}</h3>
                  <div className="text-[13px] text-[#475569]">{s.desc}</div>
                </div>
              </motion.li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}
